import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Help as HelpIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Description as MarkdownIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useNotification } from '@ipc/ipc-components';
import { ModuleNames } from '../../common/constants/userConstants';
import HelpContentModal from './components/HelpContentModal';
import HelpEditModal from './components/HelpEditModal';
import HelpGlobalEditModal, { GLOBAL_MODULE_ID } from './components/HelpGlobalEditModal';
import { Sidebar } from '../../common/components/Sidebar';
import SectionBar from '../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../hooks';
import { MarkdownService, MarkdownContent, MarkdownAttachment } from './services/markdownService';
import { sanitizeErrorMessage } from './utils/contentSanitizer';
import * as Styled from './Help.styled';

interface HelpContent {
  id: string;
  moduleId: string;
  title: string;
  content: string;
  contentType: 'markdown' | 'video';
  videoUrl?: string;
  createdAt: string;
  updatedAt: string;
  pageUrl?: string;
  dataTestId?: string;
  attachmentList?: MarkdownAttachment[];
}

const Help: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { successNotification, errorNotification } = useNotification();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [selectedContent, setSelectedContent] = useState<HelpContent | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<HelpContent | null>(null);
  const [isGlobalEditModalOpen, setIsGlobalEditModalOpen] = useState(false);
  const [editingGlobalContent, setEditingGlobalContent] = useState<HelpContent | null>(null);
  const [helpContents, setHelpContents] = useState<HelpContent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<HelpContent | null>(null);

  // Load all help content from API
  useEffect(() => {
    const loadAllHelpContent = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const content = await refreshHelpContents();
        setHelpContents(content);
      } catch (err) {
        const errorMessage = sanitizeErrorMessage(err);
        setError(errorMessage);
        console.error('Error loading help content:', err);
        setHelpContents([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllHelpContent();
  }, []);

  const getModuleHelpContent = (moduleId: string): HelpContent[] => {
    return helpContents.filter((content) => content.moduleId === moduleId);
  };

  const handleViewContent = async (content: HelpContent) => {
    // Global attachment entries open the edit modal directly (no view modal)
    if (content.moduleId === GLOBAL_MODULE_ID) {
      handleEditContent(content);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      // Fetch the full markdown content using the ID
      const fullContent = await MarkdownService.getMarkdownById(content.id);

      // Convert the API response to our HelpContent format
      const viewingContent: HelpContent = {
        id: fullContent.id,
        moduleId: fullContent.moduleid,
        title: fullContent.title,
        content: fullContent.content,
        contentType: fullContent.type === 'video' ? 'video' : 'markdown',
        videoUrl: fullContent.type === 'video' ? fullContent.content : undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        pageUrl: fullContent.pageurl,
        dataTestId: fullContent.datatestid,
        attachmentList: fullContent.attachmentList || [],
      };

      setSelectedContent(viewingContent);
      setIsViewModalOpen(true);
    } catch (err) {
      setError(sanitizeErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditContent = async (content: HelpContent) => {
    // Global entries open the simplified attachment modal
    if (content.moduleId === GLOBAL_MODULE_ID) {
      setIsLoading(true);
      try {
        const fullContent = await MarkdownService.getMarkdownById(content.id);
        setEditingGlobalContent({
          id: fullContent.id,
          moduleId: GLOBAL_MODULE_ID,
          title: fullContent.title,
          content: fullContent.content,
          contentType: 'markdown',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          dataTestId: fullContent.datatestid,
          pageUrl: fullContent.pageurl,
          attachmentList: fullContent.attachmentList || [],
        });
        setIsGlobalEditModalOpen(true);
      } catch (err) {
        setError(sanitizeErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      // Fetch the full markdown content using the ID
      const fullContent = await MarkdownService.getMarkdownById(content.id);

      // Convert the API response to our HelpContent format
      const editingContent: HelpContent = {
        id: fullContent.id,
        moduleId: fullContent.moduleid,
        title: fullContent.title,
        content: fullContent.content,
        contentType: fullContent.type === 'video' ? 'video' : 'markdown',
        videoUrl: fullContent.type === 'video' ? fullContent.content : undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        pageUrl: fullContent.pageurl,
        dataTestId: fullContent.datatestid,
        attachmentList: fullContent.attachmentList || [],
      };

      setEditingContent(editingContent);
      setIsEditModalOpen(true);
    } catch (err) {
      setError(sanitizeErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddContent = (moduleId: string) => {
    if (moduleId === GLOBAL_MODULE_ID) {
      setEditingGlobalContent(null);
      setIsGlobalEditModalOpen(true);
      return;
    }
    const newContent: HelpContent = {
      id: Date.now().toString(),
      moduleId,
      title: '',
      content: '',
      contentType: 'markdown',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      pageUrl: '',
      dataTestId: '',
    };
    setEditingContent(newContent);
    setIsEditModalOpen(true);
  };

  const handleDeleteContent = (content: HelpContent) => {
    setContentToDelete(content);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!contentToDelete) return;

    setIsLoading(true);
    setError(null);

    try {
      await MarkdownService.deleteMarkdown(contentToDelete.id);

      // Refresh the content list from API
      const convertedContent = await refreshHelpContents();
      setHelpContents(convertedContent);

      setDeleteDialogOpen(false);
      setContentToDelete(null);
    } catch (err) {
      setError(sanitizeErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setContentToDelete(null);
  };

  const refreshHelpContents = async (): Promise<HelpContent[]> => {
    const refreshedContent = await MarkdownService.getAllMarkdowns();
    return refreshedContent.map(item => ({
      id: item.id,
      moduleId: item.moduleid,
      title: item.title,
      content: item.content,
      contentType: item.type === 'video' ? 'video' : 'markdown',
      videoUrl: item.type === 'video' ? item.content : undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      pageUrl: item.pageurl,
      dataTestId: item.datatestid,
      attachmentList: item.attachmentList || [],
    }));
  };

  const handleSaveContent = async (content: HelpContent & { imageList?: any[]; videoList?: any[]; attachmentList?: any[] }) => {
    setIsLoading(true);
    setError(null);

    const markdownData = {
      id: content.id,
      title: content.title,
      datatestid: content.dataTestId || '',
      pageurl: content.pageUrl || '',
      moduleid: content.moduleId,
      type: content.contentType,
      content: content.content,
      enabled: true,
      imageList: content.imageList || [],
      videoList: content.videoList || [],
      attachmentList: content.attachmentList || [],
    };

    let uploadError: unknown = null;

    // 1. Attempt save
    try {
      await MarkdownService.uploadMarkdown(markdownData);
    } catch (err) {
      uploadError = err;
      console.error('[Help] uploadMarkdown threw (may still have saved):', err);
    }

    // 2. Refresh list regardless of whether uploadMarkdown threw
    try {
      const refreshed = await refreshHelpContents();
      const savedSuccessfully = refreshed.some(
        (item) =>
          item.title === content.title &&
          item.pageUrl === content.pageUrl &&
          item.moduleId === content.moduleId
      );

      setHelpContents(refreshed);

      if (savedSuccessfully) {
        // Data IS on the server — treat as success even if uploadMarkdown threw
        setIsEditModalOpen(false);
        setEditingContent(null);
        successNotification('Help content saved successfully');
      } else if (uploadError) {
        // Data is NOT on the server and the upload threw — real error
        const errorMessage = sanitizeErrorMessage(uploadError);
        setError(errorMessage);
        errorNotification(errorMessage);
      }
    } catch (refreshErr) {
      console.error('[Help] Error refreshing help content:', refreshErr);
      if (uploadError) {
        // Both failed — show the original save error
        const errorMessage = sanitizeErrorMessage(uploadError);
        setError(errorMessage);
        errorNotification(errorMessage);
      } else {
        // Save succeeded, refresh failed — still treat as success
        setIsEditModalOpen(false);
        setEditingContent(null);
        successNotification('Help content saved successfully');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const modules = Object.values(ModuleNames);

  const GLOBAL_MODULE_NAME = t('help.globalModule', 'Global');
  const globalContents = helpContents.filter(
    (c) => c.moduleId === GLOBAL_MODULE_ID
  );

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/home');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='help-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='Help Center'
            subtitle='Documentation'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            data-testid='help-section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                navItems={navItems}
                data-testid='help-sidebar'
              />
            </Box>
          )}

          {/* Main Content */}
          <Styled.MainContent sidebarOpen={sidebarOpen} data-testid='help-main-content'>
            <Styled.Container>
              {/* Error Display */}
              {error && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    <Typography variant="body2" component="div">
                      {error}
                    </Typography>
                  </Alert>
                </Box>
              )}

              {/* Loading Indicator */}
              {isLoading && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('help.loading', 'Loading help content...')}
                  </Typography>
                </Box>
              )}

              {/* Bulk import entry point */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant='contained'
                  startIcon={<CloudUploadIcon />}
                  onClick={() => navigate('/help/import')}
                  data-testid='help-import-entry-button'
                >
                  {t('help.importGuide', 'Import full guide (.zip)')}
                </Button>
              </Box>

              <Grid container spacing={3}>
                {/* Global card — for content not tied to a specific module */}
                <Grid item xs={12} md={6} lg={4} key='global'>
                  <Card sx={{ borderTop: '3px solid', borderColor: 'secondary.main' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AttachFileIcon sx={{ color: 'secondary.main', fontSize: '1.1rem' }} />
                          <Typography variant='h6' component='h2' gutterBottom sx={{ mb: 0 }}>
                            {GLOBAL_MODULE_NAME}
                          </Typography>
                        </Box>
                        <Tooltip title={t('help.addContent', 'Add Help Content')}>
                          <IconButton
                            size='small'
                            onClick={() => handleAddContent(GLOBAL_MODULE_NAME)}
                            sx={{ color: 'secondary.main' }}
                            data-testid='help-global-add-button'
                          >
                            <HelpIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {globalContents.length === 0 ? (
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                          {t('help.noContent', 'No help content available')}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {globalContents.map((content) => (
                            <Box key={content.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                icon={content.contentType === 'video' ? <PlayIcon /> : <MarkdownIcon />}
                                label={
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant='caption' sx={{ fontWeight: 'bold' }}>
                                        {content.title}
                                      </Typography>
                                      {content.attachmentList && content.attachmentList.length > 0 && (
                                        <Tooltip title={t('help.attachmentCount', '{{count}} attachment(s)', { count: content.attachmentList.length })}>
                                          <AttachFileIcon sx={{ fontSize: '0.75rem', color: 'secondary.main' }} />
                                        </Tooltip>
                                      )}
                                    </Box>
                                    {content.pageUrl && (
                                      <Typography variant='caption' color='text.secondary'>
                                        {content.pageUrl}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                size='small'
                                variant='outlined'
                                onClick={() => handleViewContent(content)}
                                sx={{
                                  flex: 1,
                                  justifyContent: 'flex-start',
                                  height: 'auto',
                                  '& .MuiChip-label': { display: 'block', whiteSpace: 'normal', textAlign: 'left' },
                                }}
                              />
                              <Tooltip title={t('help.editContent', 'Edit Content')}>
                                <IconButton size='small' onClick={() => handleEditContent(content)}>
                                  <EditIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('help.deleteContent', 'Delete Content')}>
                                <IconButton size='small' onClick={() => handleDeleteContent(content)} sx={{ color: 'error.main' }}>
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<AddIcon />}
                        onClick={() => handleAddContent(GLOBAL_MODULE_NAME)}
                        sx={{ mt: 2, borderColor: 'secondary.main', color: 'secondary.main' }}
                        fullWidth
                        data-testid='help-global-add-content-button'
                      >
                        {t('help.addContent', 'Add Help Content')}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Per-module cards */}
                {modules.map((moduleName) => {
                  const moduleContents = getModuleHelpContent(moduleName);

                  return (
                    <Grid item xs={12} md={6} lg={4} key={moduleName}>
                      <Card>
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              mb: 2,
                            }}
                          >
                            <Typography variant='h6' component='h2' gutterBottom>
                              {moduleName}
                            </Typography>
                            <Tooltip title={t('help.addContent', 'Add Help Content')}>
                              <IconButton
                                size='small'
                                onClick={() => handleAddContent(moduleName)}
                                sx={{ color: 'primary.main' }}
                              >
                                <HelpIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {moduleContents.length === 0 ? (
                            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                              {t('help.noContent', 'No help content available')}
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {moduleContents.map((content) => (
                                <Box
                                  key={content.id}
                                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                  <Chip
                                    icon={
                                      content.contentType === 'video' ? (
                                        <PlayIcon />
                                      ) : (
                                        <MarkdownIcon />
                                      )
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Typography variant='caption' sx={{ fontWeight: 'bold' }}>
                                            {content.title}
                                          </Typography>
                                          {content.attachmentList && content.attachmentList.length > 0 && (
                                            <Tooltip title={t('help.attachmentCount', '{{count}} attachment(s)', { count: content.attachmentList.length })}>
                                              <AttachFileIcon sx={{ fontSize: '0.75rem', color: 'primary.main' }} />
                                            </Tooltip>
                                          )}
                                        </Box>
                                        {content.pageUrl && (
                                          <Typography variant='caption' color='text.secondary'>
                                            {content.pageUrl}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                    size='small'
                                    variant='outlined'
                                    onClick={() => handleViewContent(content)}
                                    sx={{ 
                                      flex: 1, 
                                      justifyContent: 'flex-start',
                                      height: 'auto',
                                      '& .MuiChip-label': {
                                        display: 'block',
                                        whiteSpace: 'normal',
                                        textAlign: 'left',
                                      }
                                    }}
                                  />
                                  <Tooltip title={t('help.editContent', 'Edit Content')}>
                                    <IconButton
                                      size='small'
                                      onClick={() => handleEditContent(content)}
                                    >
                                      <EditIcon fontSize='small' />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('help.deleteContent', 'Delete Content')}>
                                    <IconButton
                                      size='small'
                                      onClick={() => handleDeleteContent(content)}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <DeleteIcon fontSize='small' />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ))}
                            </Box>
                          )}

                          <Button
                            variant='outlined'
                            size='small'
                            startIcon={<HelpIcon />}
                            onClick={() => handleAddContent(moduleName)}
                            sx={{ mt: 2 }}
                            fullWidth
                          >
                            {t('help.addContent', 'Add Help Content')}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {/* View Help Content Modal */}
              <HelpContentModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                content={selectedContent}
              />

              {/* Edit Help Content Modal */}
              <HelpEditModal
                isOpen={isEditModalOpen}
                onClose={() => {
                  setIsEditModalOpen(false);
                  setEditingContent(null);
                }}
                content={editingContent}
                onSave={handleSaveContent}
              />

              {/* Global Attachment Modal */}
              <HelpGlobalEditModal
                isOpen={isGlobalEditModalOpen}
                onClose={() => {
                  setIsGlobalEditModalOpen(false);
                  setEditingGlobalContent(null);
                }}
                content={editingGlobalContent}
                onSave={handleSaveContent}
              />

              {/* Delete Confirmation Dialog */}
              <Dialog
                open={deleteDialogOpen}
                onClose={cancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
              >
                <DialogTitle id="delete-dialog-title">
                  {t('help.deleteConfirmTitle', 'Delete Help Content')}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="delete-dialog-description">
                    {t('help.deleteConfirmMessage', 'Are you sure you want to delete this help content? This action cannot be undone.')}
                    {contentToDelete && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {contentToDelete.title}
                        </Typography>
                        {contentToDelete.pageUrl && (
                          <Typography variant="caption" color="text.secondary">
                            {contentToDelete.pageUrl}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={cancelDelete} color="primary">
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button 
                    onClick={confirmDelete} 
                    color="error" 
                    variant="contained"
                    disabled={isLoading}
                  >
                    {isLoading ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
                  </Button>
                </DialogActions>
              </Dialog>
            </Styled.Container>
          </Styled.MainContent>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Help;
